import { Clip } from '../types';

// Derive an 11-char YouTube ID from a parent clip ID or video URL, if present
function deriveYoutubeId(parentClipId?: string, videoUrl?: string): string | undefined {
  const candidate = parentClipId?.trim() || videoUrl?.trim() || '';
  if (/^[\w-]{11}$/.test(candidate)) return candidate;
  const match = candidate.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : undefined;
}

// Normalize a D1 custom_clips row into a Clip (shared by fetch/claim/update paths)
function normalizeClipRow(c: any): Clip {
  const ytId = c.parent_clip_id || c.parentClipId || '';
  const poster = c.poster_url || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : `/api/media/clips/${c.id}.jpg`);
  return {
    id: c.id,
    title: c.title,
    sourceUrl: c.video_url || c.videoUrl || (ytId ? `https://www.youtube.com/watch?v=${ytId}` : ''),
    videoUrl: c.video_url || c.videoUrl || `/api/media/clips/${c.id}.mp4`,
    audioUrl: c.audio_url || c.audioUrl || `/api/media/stems/${c.id}/bgm.mp3`,
    vocalsUrl: c.vocals_url || c.vocalsUrl || `/api/media/stems/${c.id}/vocals.mp3`,
    posterUrl: poster,
    thumbnailUrl: poster,
    youtubeId: ytId,
    parentClipId: ytId || c.parent_clip_id || undefined,
    duration: c.duration,
    trimStart: c.trim_start !== undefined ? c.trim_start : (c.trimStart || 0),
    trimEnd: c.trim_end !== undefined ? c.trim_end : (c.trimEnd || c.duration || 15),
    category: `R2 Saved Clip`,
    hasStems: c.has_stems !== undefined ? Boolean(c.has_stems) : (c.hasStems !== undefined ? Boolean(c.hasStems) : true),
    playbackMode: 'html5'
  };
}

export interface SaveClipPayload {
  parentClipId: string;
  title: string;
  videoUrl: string;
  audioUrl?: string;
  posterUrl?: string;
  anchorTime: number;
  prePadding: number;
  postPadding: number;
  trimStart: number;
  trimEnd: number;
  userName?: string;
}

export async function extractClipFromUrl(url: string, trimStart: number = 0, trimEnd: number = 20): Promise<Clip> {
  try {
    const res = await fetch('/api/curator/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, trimStart, trimEnd })
    });

    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && data.clip) {
        return data.clip;
      }
    }
  } catch (err) {
    console.warn('API extractor notice:', err);
  }

  const ytId = deriveYoutubeId(undefined, url);
  return {
    id: `clip-${Date.now()}`,
    title: 'Extracted Scene',
    sourceUrl: url,
    videoUrl: url,
    youtubeId: ytId,
    posterUrl: ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '',
    trimStart,
    trimEnd,
    duration: Math.max(15, Math.min(30, trimEnd - trimStart)),
    category: 'Captured Scene'
  };
}

const TEMP_VAULT_KEY = 'soulsync_temp_vault_clips';

export const MAX_TEMP_CLIPS = 3;

export function getTempVaultClips(): Clip[] {
  try {
    const raw = sessionStorage.getItem(TEMP_VAULT_KEY) || localStorage.getItem(TEMP_VAULT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveTempVaultClip(clip: Clip): void {
  try {
    const prev = getTempVaultClips();
    // Enforce maximum of 3 clips in the anonymous Temp Vault
    const updated = [clip, ...prev.filter(c => c.id !== clip.id)].slice(0, MAX_TEMP_CLIPS);
    sessionStorage.setItem(TEMP_VAULT_KEY, JSON.stringify(updated));
    localStorage.setItem(TEMP_VAULT_KEY, JSON.stringify(updated));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('soulsync:vault_updated'));
    }
  } catch {
    // ignore
  }
}

export function deleteTempVaultClip(clipId: string): void {
  try {
    const prev = getTempVaultClips();
    const updated = prev.filter(c => c.id !== clipId);
    sessionStorage.setItem(TEMP_VAULT_KEY, JSON.stringify(updated));
    localStorage.setItem(TEMP_VAULT_KEY, JSON.stringify(updated));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('soulsync:vault_updated'));
    }
  } catch {
    // ignore
  }
}

export function clearTempVault(): void {
  try {
    sessionStorage.removeItem(TEMP_VAULT_KEY);
    localStorage.removeItem(TEMP_VAULT_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('soulsync:vault_updated'));
    }
  } catch {
    // ignore
  }
}

/**
 * Claim an existing saved clip for a user (sets user_name on the D1 row).
 * Reuses the already-baked MP4, Demucs stems, and diarization (R2 keys are
 * keyed by clipId and remain untouched). Returns the normalized clip on success.
 */
export async function claimClipForUser(clipId: string, userName: string): Promise<{ updated: boolean; clip?: Clip }> {
  if (!clipId || !userName?.trim()) return { updated: false };
  try {
    const res = await fetch('/api/curator/clip', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, userName: userName.trim() })
    });
    if (!res.ok) return { updated: false };
    const data = await res.json() as any;
    if (data.success && data.updated && data.clip) {
      return { updated: true, clip: normalizeClipRow(data.clip) };
    }
    return { updated: false };
  } catch (err) {
    console.warn('Claim clip notice:', err);
    return { updated: false };
  }
}

/**
 * Generic saved-clip update (groundwork for future clip renaming).
 * Renaming later = updateSavedClip(clipId, { title: 'New Name' }).
 */
export async function updateSavedClip(clipId: string, updates: { title?: string; userName?: string }): Promise<Clip | null> {
  if (!clipId) return null;
  try {
    const res = await fetch('/api/curator/clip', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clipId, ...updates })
    });
    if (!res.ok) return null;
    const data = await res.json() as any;
    if (data.success && data.updated && data.clip) {
      return normalizeClipRow(data.clip);
    }
    return null;
  } catch (err) {
    console.warn('Update clip notice:', err);
    return null;
  }
}

export async function syncTempVaultToUser(userName: string): Promise<Clip[]> {
  const tempClips = getTempVaultClips();
  if (tempClips.length === 0 || !userName?.trim()) return [];

  const synced: Clip[] = [];
  for (const clip of tempClips) {
    try {
      // 1. Prefer claiming the existing D1 row — keeps the baked MP4, stems,
      //    and diarization already keyed by this clipId (only user_name changes).
      const claim = await claimClipForUser(clip.id, userName.trim());
      if (claim.updated && claim.clip) {
        synced.push(claim.clip);
        continue;
      }

      // 2. Fallback: clip was never persisted server-side (offline capture) — create fresh.
      const saved = await saveClippedScene({
        parentClipId: clip.parentClipId || clip.youtubeId || clip.id,
        title: clip.title,
        videoUrl: clip.sourceUrl || clip.videoUrl,
        posterUrl: clip.posterUrl || clip.thumbnailUrl,
        anchorTime: clip.trimStart + ((clip.trimEnd - clip.trimStart) / 2),
        prePadding: 0,
        postPadding: 0,
        trimStart: clip.trimStart,
        trimEnd: clip.trimEnd,
        userName: userName.trim()
      });
      synced.push(saved);
    } catch (err) {
      console.warn('Failed to sync temp clip:', clip.id, err);
    }
  }

  // Destroy the local Temp Vault copy once migrated
  clearTempVault();
  return synced;
}

export async function saveClippedScene(payload: SaveClipPayload): Promise<Clip> {
  const duration = Math.round((payload.trimEnd - payload.trimStart) * 10) / 10;
  const clipId = `clip-${payload.parentClipId}-${Math.round(payload.anchorTime)}s`;

  const fallbackClip: Clip = {
    id: clipId,
    title: payload.title,
    sourceUrl: payload.videoUrl,
    videoUrl: `/api/media/clips/${clipId}.mp4`,
    audioUrl: payload.audioUrl,
    posterUrl: payload.posterUrl,
    parentClipId: payload.parentClipId,
    youtubeId: deriveYoutubeId(payload.parentClipId, payload.videoUrl),
    duration,
    trimStart: payload.trimStart,
    trimEnd: payload.trimEnd,
    category: `Clipped Line @ ${payload.anchorTime.toFixed(1)}s`
  };

  try {
    const res = await fetch('/api/curator/clip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && data.clip) {
        const savedClip: Clip = {
          id: data.clip.id,
          title: data.clip.title,
          sourceUrl: data.clip.sourceUrl || payload.videoUrl,
          videoUrl: data.clip.r2VideoUrl || `/api/media/clips/${data.clip.id}.mp4`,
          audioUrl: data.clip.audioUrl,
          posterUrl: data.clip.posterUrl,
          parentClipId: payload.parentClipId,
          youtubeId: deriveYoutubeId(payload.parentClipId, payload.videoUrl),
          duration: data.clip.duration,
          trimStart: data.clip.trimStart,
          trimEnd: data.clip.trimEnd,
          category: `Clipped Line @ ${payload.anchorTime.toFixed(1)}s`
        };

        if (!payload.userName) {
          saveTempVaultClip(savedClip);
        }
        return savedClip;
      }
    }
  } catch (err) {
    console.warn('Saving clip to R2/D1 notice:', err);
  }

  // If unlogged or offline, save to Temp Vault
  if (!payload.userName) {
    saveTempVaultClip(fallbackClip);
  }

  // Persist locally in localStorage so it appears in Dub Studio immediately
  try {
    const existing = JSON.parse(localStorage.getItem('soulsync_custom_clips') || '[]');
    localStorage.setItem('soulsync_custom_clips', JSON.stringify([fallbackClip, ...existing]));
  } catch {
    // ignore
  }

  return fallbackClip;
}

export async function fetchSavedClips(userName?: string): Promise<Clip[]> {
  try {
    const url = userName?.trim()
      ? `/api/curator/clip?user=${encodeURIComponent(userName.trim())}`
      : '/api/curator/clip';
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json() as any;
      if (data.success && Array.isArray(data.clips)) {
        const apiClips = data.clips.map((c: any) => normalizeClipRow(c));
        const tempClips = getTempVaultClips();
        const seenIds = new Set(apiClips.map((c: Clip) => c.id));
        const uniqueTemp = tempClips.filter((c: Clip) => !seenIds.has(c.id));
        return [...apiClips, ...uniqueTemp];
      }
    }
  } catch (err) {
    console.warn('Fetch clips notice:', err);
  }

  // Fallback to local temp vault clips if network is offline
  try {
    return getTempVaultClips();
  } catch {
    return [];
  }
}

export async function deleteSavedClip(clipId: string): Promise<boolean> {
  // Always delete from local Temp Vault
  deleteTempVaultClip(clipId);

  try {
    const res = await fetch(`/api/curator/clip?id=${encodeURIComponent(clipId)}`, {
      method: 'DELETE'
    });
    if (res.ok) {
      const existing = JSON.parse(localStorage.getItem('soulsync_custom_clips') || '[]');
      const filtered = existing.filter((c: any) => c.id !== clipId);
      localStorage.setItem('soulsync_custom_clips', JSON.stringify(filtered));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('soulsync:vault_updated'));
      }
      return true;
    }
  } catch (err) {
    console.warn('Delete clip from R2 notice:', err);
  }

  try {
    const existing = JSON.parse(localStorage.getItem('soulsync_custom_clips') || '[]');
    const filtered = existing.filter((c: any) => c.id !== clipId);
    localStorage.setItem('soulsync_custom_clips', JSON.stringify(filtered));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('soulsync:vault_updated'));
    }
    return true;
  } catch {
    return false;
  }
}

export interface UploadUserClipPayload {
  clipId?: string;
  file: File;
  title: string;
  duration: number;
  trimStart: number;
  trimEnd: number;
  rightsAffirmed: boolean;
  userName?: string;
  onProgress?: (stage: 'hashing' | 'registering' | 'uploading' | 'baking', pct: number) => void;
}

export async function computeFileHash(file: File): Promise<string> {
  try {
    const slice = file.slice(0, 1024 * 1024); // Hash first 1MB for speed
    const buffer = await slice.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
  } catch {
    return `${Date.now().toString(36)}-${file.size.toString(36)}`;
  }
}

export async function uploadUserClip(payload: UploadUserClipPayload): Promise<Clip> {
  const { clipId: preferredClipId, file, title, duration, trimStart, trimEnd, rightsAffirmed, userName, onProgress } = payload;

  if (!rightsAffirmed) {
    throw new Error('Rights warranty affirmation is required before uploading.');
  }

  if (file.size > 100 * 1024 * 1024) {
    throw new Error('File exceeds 100MB upload limit.');
  }

  // 1. Compute quick file hash
  onProgress?.('hashing', 10);
  const fileHash = await computeFileHash(file);

  // 2. Register clip & rights affirmation with Cloudflare API
  onProgress?.('registering', 25);
  const regRes = await fetch('/api/curator/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      clipId: preferredClipId,
      title,
      duration,
      trimStart,
      trimEnd,
      rightsAffirmed: true,
      fileHash,
      userName: userName?.trim() || undefined
    })
  });

  if (!regRes.ok) {
    const errData = await regRes.json().catch(() => ({ error: 'Upload registration failed' })) as any;
    throw new Error(errData.error || `Upload registration failed (${regRes.status})`);
  }

  const regData = await regRes.json() as any;
  const clipId = regData.clipId;
  const uploadUrl = regData.uploadUrl || `/api/media/clips/${clipId}.mp4`;

  // 3. Direct R2 media upload with true progress tracking via XMLHttpRequest
  onProgress?.('uploading', 0);
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
        onProgress?.('uploading', pct);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.('uploading', 100);
        resolve();
      } else {
        let errMessage = `Video storage failed (${xhr.status})`;
        try {
          const parsed = JSON.parse(xhr.responseText);
          if (parsed.error) errMessage = parsed.error;
        } catch (_) {}
        reject(new Error(errMessage));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during video storage upload.'));
    xhr.send(file);
  });

  // 4. Confirm upload & dispatch Modal baker for Demucs stems + thumbnail
  onProgress?.('baking', 75);
  const bakeRes = await fetch('/api/curator/clip', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      clipId,
      source: 'r2',
      trimStart,
      trimEnd,
      title,
      userName: userName?.trim() || undefined
    })
  });

  if (!bakeRes.ok) {
    console.warn('Bake dispatch notification notice, continuing polling readiness...');
  }

  onProgress?.('baking', 100);

  const finalClip: Clip = {
    id: clipId,
    title: title || 'User Upload',
    sourceUrl: `/api/media/clips/${clipId}.mp4`,
    videoUrl: `/api/media/clips/${clipId}.mp4`,
    audioUrl: `/api/media/stems/${clipId}/bgm.mp3`,
    vocalsUrl: `/api/media/stems/${clipId}/vocals.mp3`,
    posterUrl: `/api/media/clips/${clipId}.jpg`,
    duration: Math.round((trimEnd - trimStart) * 10) / 10,
    trimStart,
    trimEnd,
    playbackMode: 'html5',
    category: 'Uploaded Scene'
  };

  return finalClip;
}
